import type { InboxMessage } from "@/types/types";

export function cleanHtml(html: string): string {
  const quotedReplyPattern = /On\s+[^<]+<[^>]+@[^>]+>\s*wrote:/;
  const match = html.match(quotedReplyPattern);

  if (match && match.index) {
    const previousBrIndex = html.lastIndexOf("<br", match.index);
    if (previousBrIndex !== -1) {
      html = html.substring(0, previousBrIndex);
    }
  }

  return html
    .replace(/<\/?html>/g, "")
    .replace(/<\/?body>/g, "")
    .replace(/<p>/g, "<br/>")
    .replace(/<\/p>/g, "<br/>")
    .replace(/^>.*$/gm, "");
}

export function formatPlainText(text: string): string {
  if (!text.includes("<p>") && !text.includes("<div>")) {
    const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

    return normalized
      .split("\n")
      .map((line) => {
        if (line.startsWith(">")) {
          return `<blockquote class="pl-4 border-l-4 border-muted text-muted-foreground">${line.substring(
            1
          )}</blockquote>`;
        }
        return line;
      })
      .join("<br />");
  }
  return text;
}

/**
 * Email body (HTML or plain text) → readable plain text. Mirrors what the
 * inbox renders — quoted replies stripped, block breaks kept as newlines,
 * entities decoded — so the copy matches what the panel shows.
 */
export function emailBodyToText(content: string): string {
  const html = cleanHtml(formatPlainText(content ?? ""))
    // Raw newlines are insignificant in HTML (plain text already became <br />).
    .replace(/\r?\n/g, " ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr|h[1-6]|blockquote|pre)>/gi, "\n");
  const doc = new DOMParser().parseFromString(html, "text/html");
  // Quoted history is noise here — every earlier message is already in the thread.
  doc.querySelectorAll("style, script, blockquote, .gmail_quote").forEach((node) => node.remove());
  // Keep the URL behind linked text — a prompt can't follow "Sign the contract" otherwise.
  doc.querySelectorAll("a[href]").forEach((anchor) => {
    const href = anchor.getAttribute("href") ?? "";
    if (/^https?:/i.test(href) && anchor.textContent?.trim() !== href) anchor.append(` (${href})`);
  });
  return (doc.body?.textContent ?? "")
    .replace(/\u00a0/g, " ")
    .split("\n")
    .map((line) => line.replace(/[ \t]{2,}/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    // A dangling "On <date>, <name> <email> wrote:" header left behind by a removed quote.
    .replace(/\n+On [\s\S]{0,300}?wrote:\s*$/, "")
    .trim();
}

interface ThreadPromptOptions {
  messages: InboxMessage[];
  campaignName?: string | null;
  creator?: { _id?: string; username?: string; email?: string; status?: string } | null;
  /** Addresses that count as "us": the campaign owner's inbox, Amy's mailbox. */
  ourEmails: Array<string | null | undefined>;
  /** A pending Amy draft the creator has NOT received yet. */
  unsentDraft?: string | null;
}

/**
 * The open thread as plain text for pasting into an LLM prompt: a short
 * header (creator, status, subject), then every message oldest-first with
 * the sender's role, recipients, date, attachment names and the body.
 */
export function formatThreadForPrompt({
  messages,
  campaignName,
  creator,
  ourEmails,
  unsentDraft,
}: ThreadPromptOptions): string {
  const ours = ourEmails.filter((e): e is string => !!e).map((e) => e.toLowerCase());
  const creatorEmail = creator?.email?.toLowerCase();
  const roleOf = (from: string): string | null => {
    const sender = from.toLowerCase();
    if (creatorEmail && sender.includes(creatorEmail)) return "creator";
    if (ours.some((e) => sender.includes(e))) return "us";
    return null;
  };

  const lines: string[] = [
    `# Email thread with @${creator?.username || "creator"}${campaignName ? ` — campaign "${campaignName}"` : ""}`,
  ];
  if (creator?._id) lines.push(`Campaign creator id: ${creator._id}`);
  if (creator?.email) lines.push(`Creator: ${creator.email}`);
  if (creator?.status) lines.push(`Status: ${creator.status}`);
  const subject = messages[0]?.subject;
  if (subject) lines.push(`Subject: ${subject}`);
  lines.push(`Messages: ${messages.length} (oldest first)`);

  messages.forEach((message, index) => {
    const from = typeof message.from === "string" ? message.from : "";
    const role = roleOf(from);
    lines.push("", "---", "", `## Message ${index + 1}${role ? ` (${role})` : ""}`);
    lines.push(`From: ${from}`);
    if (message.to) lines.push(`To: ${message.to}`);
    if (message.cc) lines.push(`Cc: ${message.cc}`);
    // Amy's route keeps the ISO stamp beside the relative label; Flask only has the label.
    const date = message.sent_at_iso || message.sent_at;
    if (date) lines.push(`Date: ${date}`);
    if (message.attachments?.length) {
      lines.push(`Attachments: ${message.attachments.map((a) => a.file_name).join(", ")}`);
    }
    lines.push("", emailBodyToText(message.content ?? "") || "(empty message)");
  });

  if (unsentDraft) {
    lines.push("", "---", "", "## Unsent draft (the creator has not received this)", "", emailBodyToText(unsentDraft));
  }

  return `${lines.join("\n").trim()}\n`;
}
